import { Autocomplete, type AutocompleteProps, TextField } from "@mui/material";

import { Option } from "api/common/types/common.types";
import { useFieldContext } from "components/Forms/AppForm";
import { isNumber } from "utils/typeGuards";

import ErrorMessage from "../../helpers/ErrorMessage";

import { useNormalizedOptions } from "./UseNormalizedOptions";

type SelectFieldProps = Omit<
  AutocompleteProps<Option, false, false, false>,
  "renderInput" | "options"
> & {
  options: Option[] | string[];
  label: string;
  required?: boolean;
};

const getEmptyOption = (options: Option[]): Option => {
  const firstOption = options[0];
  const valueIsNumber = isNumber(firstOption?.value);
  return valueIsNumber ? { value: 0, label: "" } : { value: "", label: "" };
};

const AutocompleteSelect = ({
  options = [],
  label,
  required = false,
  ...props
}: SelectFieldProps) => {
  const field = useFieldContext<Option>();
  const normalizedOptions = useNormalizedOptions(options);

  return (
    <>
      <Autocomplete
        disablePortal
        size="small"
        options={normalizedOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={field.state.meta.errors.length > 0}
            required={required}
          />
        )}
        value={field.state.value || getEmptyOption(normalizedOptions)}
        onChange={(_e, value) => {
          field.handleChange(value ?? getEmptyOption(normalizedOptions));
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        {...props}
      />
      <ErrorMessage />
    </>
  );
};
export default AutocompleteSelect;
